import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { isStringEmpty } from '@common/helpers';
import {
  BLIND,
  DEAF,
  WHEEL_CHAIR_BATTERY_CODE,
  WHEEL_CHAIR_MANUAL_CODE,
} from '@common/helpers/constants/my-booking/passenger-information-input-modal.constant';
import { MListData } from '@common/interfaces';
import {
  SupportInformationData,
  SupportInformationParts,
} from '@common/interfaces/mybooking/modals/passenger-information-input/passenger-information.interface';
import { PassengerInformationInputModalStoreService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal-store.service';
import { PassengerInformationInputModalCommonService } from '@common/services/my-booking/passenger-information-input-modal/passenger-information-input-modal.common.service';
import { SupportInformationService } from '@common/services/my-booking/passenger-information-input-modal/support-information.service';
import { StaticMsgPipe } from '@lib/pipes/static-msg/static-msg.pipe';
import { CommonLibService } from '@lib/services';
import { GetBasicReservationInformationResponseDataPassengerSupport } from 'src/sdk-amcmember';
import { GetOrderResponseData } from 'src/sdk-servicing';
import { SpecialAssistanceRequestModalComponent } from '../../../special-assistance-request-modal/special-assistance-request-modal.component';

@Component({
  selector: 'asw-support-information',
  templateUrl: './support-information.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportInformationComponent {
  @Input()
  get data(): SupportInformationData | undefined {
    return this._data;
  }
  set data(value: SupportInformationData | undefined) {
    this._data = value;
    this.onInit();
  }
  private _data?: SupportInformationData;

  @Input() travelerId?: string;

  @Output()
  dataChange: EventEmitter<SupportInformationData> = new EventEmitter<SupportInformationData>();

  @Input()
  get parts(): SupportInformationParts | undefined {
    return this._parts;
  }
  set parts(value: SupportInformationParts | undefined) {
    this._parts = value;
    this.onInit();
  }
  private _parts?: SupportInformationParts;

  public isOpen: boolean = false;

  isNeededWheelchairDisplayValue(isNeededWheelchairDisplayValue?: string): string {
    return this._staticMsg.transform(isNeededWheelchairDisplayValue ?? '');
  }

  isWheelchairFoldableDisplayValue(isWheelchairFoldableDisplayValue?: string): string {
    return this._staticMsg.transform(isWheelchairFoldableDisplayValue ?? '');
  }

  wheelchairTypeDisplayValue(wheelchairTypeDisplayValue?: string): string {
    return this._staticMsg.transform(wheelchairTypeDisplayValue ?? '');
  }

  wheelchairSizeLabel(wheelchairDepth?: number, wheelchairWidth?: number, wheelchairHeight?: number) {
    return this._staticMsg.transform('label.wheelchairSize', {
      '0': wheelchairDepth ?? '',
      '1': wheelchairWidth ?? '',
      '2': wheelchairHeight ?? '',
    });
  }

  wheelchairWeightLabel(wheelchairWeight?: number) {
    return this._staticMsg.transform('label.wheelchairWeight', {
      '0': wheelchairWeight ?? '',
    });
  }

  get pregnantDoctorPhone() {
    return this.data?.pregnantDoctorPhoneNumber;
  }

  get pnr(): GetOrderResponseData | undefined {
    return this._s04StoreSvc.pnr();
  }

  constructor(
    private _change: ChangeDetectorRef,
    private _staticMsg: StaticMsgPipe,
    private _datePipe: DatePipe,
    private _service: SupportInformationService,
    private _s04CommonSvc: PassengerInformationInputModalCommonService,
    private _s04StoreSvc: PassengerInformationInputModalStoreService,
    private _common: CommonLibService
  ) {}

  onInit() {
    if (!this.data || !this.parts) {
      return;
    }

    this.isOpen =
      (this.data.blind ?? false) ||
      (this.data.walk ?? false) ||
      (this.data.deaf ?? false) ||
      (this.data.isPregnant ?? false);

    this._change.markForCheck();
  }

  setBasicInfoToSupportInfo(supports?: GetBasicReservationInformationResponseDataPassengerSupport) {
    if (!supports) {
      return;
    }
    const code = supports?.walking?.degreeSpecialServiceRequest?.code ?? '';

    const pd006: Array<MListData> = this._s04CommonSvc.masterData.listDataAll.filter(
      (data) => data.data_code === 'PD_006' && data.lang === this._s04CommonSvc.lang
    );
    const pd960: Array<MListData> = this._s04CommonSvc.masterData.listDataAll.filter(
      (data) => data.data_code === 'PD_960' && data.lang === this._s04CommonSvc.lang
    );
    this.isOpen = true;
    this.data!.walk = !isStringEmpty(code);
    this.data!.blind = supports?.blind === BLIND;
    this.data!.deaf = supports?.deaf === DEAF;
    this.data!.walkLevel = code;
    this.data!.walkLevelDisplayValue = pd006?.find((pd) => pd?.value === code)?.display_content ?? '';
    this.data!.wheelchairData![0].isNeededWheelchair = supports?.wheelchair?.isNeeded === '1';
    this.data!.wheelchairData![0]!.isNeededWheelchairDisplayValue = this._s04CommonSvc.getBringWheelchairDisplayValue(
      supports?.wheelchair?.isNeeded === '1'
    );
    this.data!.wheelchairData![0]!.isWheelchairFoldable = supports?.wheelchair?.isFoldable === '2';
    this.data!.wheelchairData![0]!.isWheelchairFoldableDisplayValue =
      this._s04CommonSvc.getWheelchairFoldableDisplayValue(supports?.wheelchair?.isFoldable === '2');
    const wheelchairType =
      supports?.wheelchair?.type === '1'
        ? WHEEL_CHAIR_BATTERY_CODE
        : supports?.wheelchair?.type === '2'
        ? WHEEL_CHAIR_MANUAL_CODE
        : '';
    this.data!.wheelchairData![0]!.wheelchairType = wheelchairType;
    this.data!.wheelchairData![0]!.wheelchairTypeDisplayValue =
      this._s04CommonSvc.getWheelchairTypeDisplayValue(wheelchairType);
    this.data!.wheelchairData![0]!.wheelchairBatteryType = supports?.wheelchair?.batteryType ?? '';
    this.data!.wheelchairData![0]!.wheelchairBatteryTypeDisplayValue =
      pd960.find((pd) => pd.value === supports?.wheelchair?.batteryType)?.display_content ?? '';
    this.data!.wheelchairData![0]!.wheelchairDepth = parseInt(supports?.wheelchair?.depth ?? '0', 10);
    this.data!.wheelchairData![0]!.wheelchairWidth = parseInt(supports?.wheelchair?.width ?? '0', 10);
    this.data!.wheelchairData![0]!.wheelchairHeight = parseInt(supports?.wheelchair?.height ?? '0', 10);
    this.data!.wheelchairData![0]!.wheelchairWeight = parseInt(supports?.wheelchair?.weight ?? '0', 10);

    this._change.markForCheck();
    this.dataChange.emit(this.data);
  }

  update(value: SupportInformationData) {
    this.data!.isSupportRegistered = value.isSupportRegistered;
    this.data!.walk = value.walk;
    this.data!.blind = value.blind;
    this.data!.deaf = value.deaf;
    this.data!.walkLevel = value.walkLevel;
    this.data!.walkLevelDisplayValue = value.walkLevelDisplayValue;
    this.data!.wheelchairData![0].isNeededWheelchair = value.isNeededWheelchair;
    this.data!.wheelchairData![0]!.isNeededWheelchairDisplayValue = value.isNeededWheelchairDisplayValue;
    this.data!.wheelchairData![0]!.isWheelchairFoldable = value.isWheelchairFoldable;
    this.data!.wheelchairData![0]!.isWheelchairFoldableDisplayValue = value.isWheelchairFoldableDisplayValue;
    this.data!.wheelchairData![0]!.wheelchairType = value.wheelchairType;
    this.data!.wheelchairData![0]!.wheelchairTypeDisplayValue = value.wheelchairTypeDisplayValue;
    this.data!.wheelchairData![0]!.wheelchairBatteryType = value.wheelchairBatteryType;
    this.data!.wheelchairData![0]!.wheelchairBatteryTypeDisplayValue = value.wheelchairBatteryTypeDisplayValue;
    this.data!.wheelchairData![0]!.wheelchairDepth = value.wheelchairDepth;
    this.data!.wheelchairData![0]!.wheelchairWidth = value.wheelchairWidth;
    this.data!.wheelchairData![0]!.wheelchairHeight = value.wheelchairHeight;
    this.data!.wheelchairData![0]!.wheelchairWeight = value.wheelchairWeight;
    this.data!.isPregnant = value.isPregnant;
    this.data!.pregnantDoctorName = value.pregnantDoctorName;
    this.data!.pregnantDoctorCountryPhoneExtension = value.pregnantDoctorCountryPhoneExtension;
    this.data!.pregnantDoctorPhoneNumber = value.pregnantDoctorPhoneNumber;
    this.data!.pregnantDoctorEncryptedPhoneNumber = value.pregnantDoctorEncryptedPhoneNumber;
    this.data!.pregnantDueDate = value.pregnantDueDate;
    this.data!.pregnantCondition = value.pregnantCondition;
    this._change.markForCheck();
    this.dataChange.emit(this.data);
  }

  // (31) サポート情報追加ボタン押下処理　※サポート情報表示パーツ
  onClickShowSupport() {
    if (!this.data || !this.parts) {
      return;
    }

    const submitEvent = (value: SupportInformationData) => {
      this.isOpen = true;
      this.update(value);
      this._change.markForCheck();
    };

    this._service.handleClickEditSupportInformation(
      this.data,
      this.parts,
      SpecialAssistanceRequestModalComponent,
      submitEvent,
      this.isDisableDegreeOfWaling
    );
  }

  // (35) サポート情報編集ボタン押下処理　※サポート情報表示パーツ
  onClickEditSupport() {
    if (!this.data || !this.parts) return;

    const submitEvent = (value: SupportInformationData) => {
      this.update(value);
      this._change.markForCheck();
    };

    this._service.handleClickEditSupportInformation(
      this.data,
      this.parts,
      SpecialAssistanceRequestModalComponent,
      submitEvent,
      this.isDisableDegreeOfWaling
    );
  }

  get nhGroupAndArrivalUS(): boolean {
    return (
      this.pnr?.air?.bounds?.some((bound) => bound?.flights?.some((flight) => !Boolean(flight?.isNhGroupOperated))) ??
      false
    );
  }

  get pregnantDueDate(): string {
    return this._datePipe.transform(this.data?.pregnantDueDate, 'YYYY/MM/dd') ?? '';
  }

  get pregnantCondition(): string {
    const pd020 = this._s04CommonSvc.masterData.listDataAll.filter(
      (data) => data.data_code === 'PD_020' && data.lang === this._s04CommonSvc.lang
    );
    return pd020?.find((item) => item?.value === this.data?.pregnantCondition)?.display_content ?? '';
  }

  get pregnantDoctors() {
    return (this.pnr?.travelerSupports as any)?.[this.travelerId ?? '']?.pregnant?.doctor;
  }

  get pregnantConditions() {
    const pd020 = this._s04CommonSvc.masterData.listDataAll.filter(
      (data) => data.data_code === 'PD_020' && data.lang === this._s04CommonSvc.lang
    );
    const conditons: string[] = [];
    ((this.pnr?.travelerSupports as any)?.[this.travelerId ?? '']?.pregnant?.condition ?? []).forEach((d: any) => {
      conditons.push(pd020?.find((item) => item?.value === d)?.display_content ?? '');
    });
    return conditons;
  }

  get pregnantDueDates(): string[] {
    const dueDates: string[] = [];
    ((this.pnr?.travelerSupports as any)?.[this.travelerId ?? '']?.pregnant?.dueDate ?? []).forEach((d: any) => {
      dueDates.push(this._datePipe.transform(d, 'YYYY/MM/dd') ?? '');
    });
    return dueDates;
  }

  get isDisableDegreeOfWaling(): boolean {
    // 提携：ストレッチャー（STCR）、車いすの機内貸出（WCOB）が登録済みの搭乗者タブのサポート情報で歩行の程度を登録／更新できないよう修正
    const specialServiceRequests =
      (this.pnr?.services?.serviceSupport as any)?.[this.travelerId ?? '']?.specialServiceRequests ?? [];
    const ssrCodes =
      this._common.aswMasterService
        .getMPropertyByKey('servicing', 'walkingDisabilityRestriction.ssrCode')
        ?.split('|') ?? [];
    return ssrCodes.some((d) => specialServiceRequests.some((s: any) => s?.code === d));
  }

  getBatteryType(batteryType: string): string {
    return batteryType;
  }

  /**
   * [12.1] サポート情報追加
   * 以下のすべてに該当する場合、表示する。
   */
  get isAddNewSupport(): boolean {
    // 1.PNR情報取得API応答.data.travelerSupports.＜当該travelers.id>.isSupportRegistered=false
    const condition1 = !((this.pnr?.travelerSupports as any)?.[this.travelerId ?? '']?.isSupportRegistered ?? false);
    // 2.PNR情報取得API応答.data.air.bounds.flights[]リストの中にisDepartedWithin24Hours=trueであるフライトが存在しない
    const condition2 = !(
      this.pnr?.air?.bounds?.some((bound) =>
        bound.flights?.some((flight) => flight.isDepartedWithin24Hours === true)
      ) ?? false
    );
    // 3.PNR情報取得API応答.data.orderEligibilities.travelerInput.isPassengerSupportEligible=true
    const condition3 = this.pnr?.orderEligibilities?.travelerInput?.isPassengerSupportEligible === true;

    return condition1 && condition2 && condition3;
  }

  get isDisplayEditSupportInfo(): boolean {
    // 1.PNR情報取得API応答.data.air.bounds.flights[]リストの中にisDepartedWithin24Hours=trueであるフライトが存在しない
    const condition1 = !(
      this.pnr?.air?.bounds?.some((bound) =>
        bound.flights?.some((flight) => flight.isDepartedWithin24Hours === true)
      ) ?? false
    );
    // 2.PNR情報取得API応答.data.orderEligibilities.travelerInput.isPassengerSupportEligible=true
    const condition2 = this.pnr?.orderEligibilities?.travelerInput?.isPassengerSupportEligible === true;
    //  ただし、下記の条件のいずれかに該当する場合は、非表示とする。
    const condition3 = this.parts?.isSupportInformationEditable ?? false;
    return condition1 && condition2 && condition3;
  }
}
